import React from 'react'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { Heading } from '@acorns/web-components'
import styled from 'styled-components'

import FullscreenTakeover from 'components/FullscreenTakeover'
import useWindowDimensions from 'hooks/useWindowDimensions'
import themeValues, { style } from 'theme'

import { CloseAccountWrapper } from '../closeAccountWrapper'
import { StyledBody, StyledCard } from '../components'
import { MergedProps } from './index'

const BodyContainer = styled.div`
  padding-bottom: 50px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    height: auto;
    min-height: 50%;
    align-self: center;
    padding-top: 50px;
  }
`
const ButtonContainer = styled.div`
  border-top: 1px solid rgb(245, 245, 245);
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    padding-top: 20px;
    position: relative;
    width: 100%;
  }
`

const Footnote = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  font-size: 12px;
  font-weight: normal;
  height: 16px;
  letter-spacing: 0px;
  line-height: 16px;
  text-align: center;
  margin-bottom: 50px;
  margin-top: 30px;
  padding-top: 50px;
  color: ${style((theme) => theme.colors.stone)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    margin-bottom: 0px;
    margin-top: 0;
  }
`

// Height is calculated by subtracting Action Strip Height from Height of Window
const Card = styled(StyledCard)<{ innerHeight?: number }>`
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    margin-top: 30px;
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    display: flex;
    justify-content: space-between;
  }
`

const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  display: flex;
  justify-content: space-between;
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
  }
`

const StyledHeading = styled(Heading)`
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  text-align: center;
  margin: 20px 0;
`

const Body = styled(StyledBody)`
  margin: 0 auto;
`

const StyledIcon = styled(AcornsIcon)`
  height: 104px;
  width: 104px;
  margin: 0 auto;
  display: inline-block;
`

type Props = Pick<MergedProps, 'handleLogoutClick' | 'hasBalance' | 'isWebView'>

export const ConfirmationScreen: React.FC<Props> = ({
  handleLogoutClick,
  hasBalance,
  isWebView,
}) => {
  const CardComponent = isWebView ? WVCard : Card
  const { height } = useWindowDimensions()
  return (
    <>
      {isWebView && (
        <FullscreenTakeover innerHeight={height}>
          <CloseAccountWrapper
            isWebView={isWebView}
            link={{ text: 'Logout', onClick: handleLogoutClick }}
            actionStripTitle={undefined}
            cancelLink={true}
            handleCancel={handleLogoutClick}
            showCancellable={true}
            closeIcon={true}
          >
            {hasBalance ? (
              <CardComponent isWebView={isWebView} innerHeight={height}>
                <BodyContainer>
                  <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />

                  <StyledHeading>Closure in progress</StyledHeading>
                  <Body>
                    Once the transfers have processed, we'll close your accounts
                    and cancel your subscription.
                  </Body>
                </BodyContainer>
                <ButtonContainer>
                  <ButtonContainer>
                    <Footnote>
                      Transfers may take up to 3-6 business days to settle after
                      your shares have been sold.
                    </Footnote>
                  </ButtonContainer>
                </ButtonContainer>
              </CardComponent>
            ) : (
              <CardComponent isWebView={isWebView} innerHeight={height}>
                <BodyContainer>
                  <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />
                  <StyledHeading>Closure in progress</StyledHeading>
                  <StyledBody>
                    We'll start processing your request and let you know as soon
                    as your account is closed.
                  </StyledBody>
                </BodyContainer>
              </CardComponent>
            )}
          </CloseAccountWrapper>
        </FullscreenTakeover>
      )}

      {!isWebView && (
        <>
          {hasBalance ? (
            <CardComponent isWebView={isWebView}>
              <BodyContainer>
                <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />
                <StyledHeading>Closure in progress</StyledHeading>
                <Body>
                  Once the transfers have processed, we'll close your accounts
                  and cancel your subscription.
                </Body>
                <MediaQuery minWidth={themeValues.breakpoints.sm}>
                  <Footnote>
                    Transfers may take up to 3-6 business days to settle after
                    your shares have been sold.
                  </Footnote>
                </MediaQuery>
              </BodyContainer>
            </CardComponent>
          ) : (
            <CardComponent isWebView={isWebView}>
              <BodyContainer>
                <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />
                <StyledHeading>Closure in progress</StyledHeading>
                <StyledBody>
                  We'll start processing your request and let you know as soon
                  as your account is closed.
                </StyledBody>
              </BodyContainer>
            </CardComponent>
          )}
        </>
      )}
    </>
  )
}
